<script setup>

import {onMounted, reactive, ref, watch} from "vue";
import OptionsField from "./optionsField.vue";
import SearchField from "./searchField.vue";
import {debounce} from "lodash";
import MultiTextField from "./multiTextField.vue";
import TitleCompletion from "./_titleCompletion.vue";

const props = defineProps({
    presetFilters: {
        type: Object,
        default: null,
    },
})

const emit = defineEmits(['nextStage', 'leadCount', 'leadCountDisplay', 'leadCountRangeDisplay', 'waiting', 'initialSearchPerformed', 'estimatedActualLeadCount'])

const searchFields = ref([
    {
        label: 'I search for people with the following job titles:',
        field: 'job_title',
        type: 'multiText',
        inputPlaceholder: 'CEO, CTO, HR etc.'
    },
    {
        label: 'I search for people in the following departments:',
        field: 'job_role',
        type: 'options',
        options: [
            { label: 'Customer Service', value: 'customer_service' },
            { label: 'Design', value: 'design' },
            { label: 'Education', value: 'education' },
            { label: 'Engineering', value: 'engineering' },
            { label: 'Finance', value: 'finance' },
            { label: 'Health', value: 'health' },
            { label: 'Human Resources', value: 'human_resources' },
            { label: 'Legal', value: 'legal' },
            { label: 'Marketing', value: 'marketing' },
            { label: 'Media', value: 'media' },
            { label: 'Operations', value: 'operations' },
            { label: 'Public Relations', value: 'public_relations' },
            { label: 'Real Estate', value: 'real_estate' },
            { label: 'Sales', value: 'sales' },
            { label: 'Trades', value: 'trades' }
        ],
        inputPlaceholder: 'Select job role'
    },
    {
        label: '... and their job title level is:',
        field: 'job_title_level',
        type: 'options',
        options: [
            { label: 'C-Level', value: 'CXO' },
            { label: 'Director', value: 'Director' },
            { label: 'Entry', value: 'Entry' },
            { label: 'Manager', value: 'Manager' },
            { label: 'Owner', value: 'Owner' },
            { label: 'Partner', value: 'Partner' },
            { label: 'Senior', value: 'Senior' },
            { label: 'Training', value: 'Training' },
            { label: 'Unpaid', value: 'Unpaid' },
            { label: 'VP', value: 'VP' }
        ],
        inputPlaceholder: 'Select job title level'
    },
    {
        label: '... they are located in:',
        field: 'location',
        type: 'search',
        searchUrl: route('api.lead-hunter.locations'),
        inputPlaceholder: 'Texas, United States or Sweden',
        resultFormatter: (result) => {
            if (result.type === 'country') {
                return {
                    label: `${result.label} (All regions)`,
                    value: result.value,
                    type: result.type,
                    states: result.states
                };
            }
            return {
                label: result.label,
                value: result.value,
                type: result.type
            };
        },
        resultSorter: (a, b) => {
            if (a.type === 'country' && b.type !== 'country') return -1;
            if (a.type !== 'country' && b.type === 'country') return 1;

            return a.label.localeCompare(b.label);
        },
        onSelect: (field, option) => {
            if (option.type === 'country' && option.states) {
                parameters[field.field] = parameters[field.field].filter(item =>
                    !option.states.some(state => state.value === item.value)
                );

                option.states.forEach(state => {
                    if (!parameters[field.field].some(item => item.value === state.value)) {
                        parameters[field.field].push({
                            label: state.label,
                            value: state.value,
                            type: 'state'
                        });
                    }
                });
            } else {
                if (!parameters[field.field].some(item => item.value === option.value)) {
                    parameters[field.field].push(option);
                }
            }
        }
    },
    {
        label: 'The company size is:',
        field: 'company_size',
        type: 'options',
        advanced: true,
        options: [
            {
                label: '1-10',
                value: '1-10'
            },
            {
                label: '11-50',
                value: '11-50'
            },
            {
                label: '51-200',
                value: '51-200'
            },
            {
                label: '201-500',
                value: '201-500'
            },
            {
                label: '501-1000',
                value: '501-1000'
            },
            {
                label: '1001-5000',
                value: '1001-5000'
            },
            {
                label: '5001-10000',
                value: '5001-10000'
            },
            {
                label: '10001+',
                value: '10001+'
            },
        ],
        inputPlaceholder: '',
    },
    {
        label: 'Job sub-role',
        field: 'job_sub_role',
        type: 'options',
        advanced: true,
        options: [
            { label: 'Accounting', value: 'accounting' },
            { label: 'Accounts', value: 'accounts' },
            { label: 'Brand Marketing', value: 'brand_marketing' },
            { label: 'Broadcasting', value: 'broadcasting' },
            { label: 'Business Development', value: 'business_development' },
            { label: 'Compensation', value: 'compensation' },
            { label: 'Content Marketing', value: 'content_marketing' },
            { label: 'Customer Success', value: 'customer_success' },
            { label: 'Data', value: 'data' },
            { label: 'Dental', value: 'dental' },
            { label: 'DevOps', value: 'devops' },
            { label: 'Doctor', value: 'doctor' },
            { label: 'Editorial', value: 'editorial' },
            { label: 'Education Administration', value: 'education_administration' },
            { label: 'Electrical', value: 'electrical' },
            { label: 'Employee Development', value: 'employee_development' },
            { label: 'Events', value: 'events' },
            { label: 'Fitness', value: 'fitness' },
            { label: 'Graphic Design', value: 'graphic_design' },
            { label: 'Information Technology', value: 'information_technology' },
            { label: 'Instructor', value: 'instructor' },
            { label: 'Investment', value: 'investment' },
            { label: 'Journalism', value: 'journalism' },
            { label: 'Judicial', value: 'judicial' },
            { label: 'Lawyer', value: 'lawyer' },
            { label: 'Logistics', value: 'logistics' },
            { label: 'Marketing Communications', value: 'marketing_communications' },
            { label: 'Mechanical', value: 'mechanical' },
            { label: 'Media Relations', value: 'media_relations' },
            { label: 'Network', value: 'network' },
            { label: 'Nursing', value: 'nursing' },
            { label: 'Office Management', value: 'office_management' },
            { label: 'Paralegal', value: 'paralegal' },
            { label: 'Pipeline', value: 'pipeline' },
            { label: 'Product', value: 'product' },
            { label: 'Product Design', value: 'product_design' },
            { label: 'Product Marketing', value: 'product_marketing' },
            { label: 'Professor', value: 'professor' },
            { label: 'Project Engineering', value: 'project_engineering' },
            { label: 'Project Management', value: 'project_management' },
            { label: 'Property Management', value: 'property_management' },
            { label: 'Quality Assurance', value: 'quality_assurance' },
            { label: 'Realtor', value: 'realtor' },
            { label: 'Recruiting', value: 'recruiting' },
            { label: 'Researcher', value: 'researcher' },
            { label: 'Security', value: 'security' },
            { label: 'Software', value: 'software' },
            { label: 'Support', value: 'support' },
            { label: 'Systems', value: 'systems' },
            { label: 'Tax', value: 'tax' },
            { label: 'Teacher', value: 'teacher' },
            { label: 'Therapy', value: 'therapy' },
            { label: 'Video', value: 'video' },
            { label: 'Web', value: 'web' },
            { label: 'Web Design', value: 'web_design' },
            { label: 'Wellness', value: 'wellness' },
            { label: 'Writing', value: 'writing' }
        ],
        inputPlaceholder: 'Select job sub-role'
    },
    {
        label: _mft('leadHunterCampaign:audience.fields.companyIndustry.label'),
        field: 'company_industry',
        type: 'options',
        advanced: true,
        options: [
            {
                label: 'Accounting',
                value: 'accounting',
            },
            {
                label: 'Airlines/aviation',
                value: 'airlines/aviation',
            },
            {
                label: 'Alternative dispute resolution',
                value: 'alternative dispute resolution',
            },
            {
                label: 'Alternative medicine',
                value: 'alternative medicine',
            },
            {
                label: 'Animation',
                value: 'animation',
            },
            {
                label: 'Apparel & fashion',
                value: 'apparel & fashion',
            },
            {
                label: 'Architecture & planning',
                value: 'architecture & planning',
            },
            {
                label: 'Arts and crafts',
                value: 'arts and crafts',
            },
            {
                label: 'Automotive',
                value: 'automotive',
            },
            {
                label: 'Aviation & aerospace',
                value: 'aviation & aerospace',
            },
            {
                label: 'Banking',
                value: 'banking',
            },
            {
                label: 'Biotechnology',
                value: 'biotechnology',
            },
            {
                label: 'Broadcast media',
                value: 'broadcast media',
            },
            {
                label: 'Building materials',
                value: 'building materials',
            },
            {
                label: 'Business supplies and equipment',
                value: 'business supplies and equipment',
            },
            {
                label: 'Capital markets',
                value: 'capital markets',
            },
            {
                label: 'Chemicals',
                value: 'chemicals',
            },
            {
                label: 'Civic & social organization',
                value: 'civic & social organization',
            },
            {
                label: 'Civil engineering',
                value: 'civil engineering',
            },
            {
                label: 'Commercial real estate',
                value: 'commercial real estate',
            },
            {
                label: 'Computer & network security',
                value: 'computer & network security',
            },
            {
                label: 'Computer games',
                value: 'computer games',
            },
            {
                label: 'Computer hardware',
                value: 'computer hardware',
            },
            {
                label: 'Computer networking',
                value: 'computer networking',
            },
            {
                label: 'Computer software',
                value: 'computer software',
            },
            {
                label: 'Construction',
                value: 'construction',
            },
            {
                label: 'Consumer electronics',
                value: 'consumer electronics',
            },
            {
                label: 'Consumer goods',
                value: 'consumer goods',
            },
            {
                label: 'Consumer services',
                value: 'consumer services',
            },
            {
                label: 'Cosmetics',
                value: 'cosmetics',
            },
            {
                label: 'Dairy',
                value: 'dairy',
            },
            {
                label: 'Defense & space',
                value: 'defense & space',
            },
            {
                label: 'Design',
                value: 'design',
            },
            {
                label: 'E-learning',
                value: 'e-learning',
            },
            {
                label: 'Education management',
                value: 'education management',
            },
            {
                label: 'Electrical/electronic manufacturing',
                value: 'electrical/electronic manufacturing',
            },
            {
                label: 'Entertainment',
                value: 'entertainment',
            },
            {
                label: 'Environmental services',
                value: 'environmental services',
            },
            {
                label: 'Events services',
                value: 'events services',
            },
            {
                label: 'Executive office',
                value: 'executive office',
            },
            {
                label: 'Facilities services',
                value: 'facilities services',
            },
            {
                label: 'Farming',
                value: 'farming',
            },
            {
                label: 'Financial services',
                value: 'financial services',
            },
            {
                label: 'Fine art',
                value: 'fine art',
            },
            {
                label: 'Fishery',
                value: 'fishery',
            },
            {
                label: 'Food & beverages',
                value: 'food & beverages',
            },
            {
                label: 'Food production',
                value: 'food production',
            },
            {
                label: 'Fund-raising',
                value: 'fund-raising',
            },
            {
                label: 'Furniture',
                value: 'furniture',
            },
            {
                label: 'Gambling & casinos',
                value: 'gambling & casinos',
            },
            {
                label: 'Government administration',
                value: 'government administration',
            },
            {
                label: 'Government relations',
                value: 'government relations',
            },
            {
                label: 'Graphic design',
                value: 'graphic design',
            },
            {
                label: 'Health, wellness and fitness',
                value: 'health, wellness and fitness',
            },
            {
                label: 'Higher education',
                value: 'higher education',
            },
            {
                label: 'Hospital & health care',
                value: 'hospital & health care',
            },
            {
                label: 'Hospitality',
                value: 'hospitality',
            },
            {
                label: 'Human resources',
                value: 'human resources',
            },
            {
                label: 'Import and export',
                value: 'import and export',
            },
            {
                label: 'Individual & family services',
                value: 'individual & family services',
            },
            {
                label: 'Industrial automation',
                value: 'industrial automation',
            },
            {
                label: 'Information services',
                value: 'information services',
            },
            {
                label: 'Information technology and services',
                value: 'information technology and services',
            },
            {
                label: 'Insurance',
                value: 'insurance',
            },
            {
                label: 'International affairs',
                value: 'international affairs',
            },
            {
                label: 'International trade and development',
                value: 'international trade and development',
            },
            {
                label: 'Internet',
                value: 'internet',
            },
            {
                label: 'Investment banking',
                value: 'investment banking',
            },
            {
                label: 'Investment management',
                value: 'investment management',
            },
            {
                label: 'Judiciary',
                value: 'judiciary',
            },
            {
                label: 'Law enforcement',
                value: 'law enforcement',
            },
            {
                label: 'Law practice',
                value: 'law practice',
            },
            {
                label: 'Legal services',
                value: 'legal services',
            },
            {
                label: 'Legislative office',
                value: 'legislative office',
            },
            {
                label: 'Libraries',
                value: 'libraries',
            },
            {
                label: 'Logistics and supply chain',
                value: 'logistics and supply chain',
            },
            {
                label: 'Luxury goods & jewelry',
                value: 'luxury goods & jewelry',
            },
            {
                label: 'Machinery',
                value: 'machinery',
            },
            {
                label: 'Management consulting',
                value: 'management consulting',
            },
            {
                label: 'Maritime',
                value: 'maritime',
            },
            {
                label: 'Market research',
                value: 'market research',
            },
            {
                label: 'Marketing and advertising',
                value: 'marketing and advertising',
            },
            {
                label: 'Mechanical or industrial engineering',
                value: 'mechanical or industrial engineering',
            },
            {
                label: 'Media production',
                value: 'media production',
            },
            {
                label: 'Medical devices',
                value: 'medical devices',
            },
            {
                label: 'Medical practice',
                value: 'medical practice',
            },
            {
                label: 'Mental health care',
                value: 'mental health care',
            },
            {
                label: 'Military',
                value: 'military',
            },
            {
                label: 'Mining & metals',
                value: 'mining & metals',
            },
            {
                label: 'Motion pictures and film',
                value: 'motion pictures and film',
            },
            {
                label: 'Museums and institutions',
                value: 'museums and institutions',
            },
            {
                label: 'Music',
                value: 'music',
            },
            {
                label: 'Nanotechnology',
                value: 'nanotechnology',
            },
            {
                label: 'Newspapers',
                value: 'newspapers',
            },
            {
                label: 'Non-profit organization management',
                value: 'non-profit organization management',
            },
            {
                label: 'Oil & energy',
                value: 'oil & energy',
            },
            {
                label: 'Online media',
                value: 'online media',
            },
            {
                label: 'Outsourcing/offshoring',
                value: 'outsourcing/offshoring',
            },
            {
                label: 'Package/freight delivery',
                value: 'package/freight delivery',
            },
            {
                label: 'Packaging and containers',
                value: 'packaging and containers',
            },
            {
                label: 'Paper & forest products',
                value: 'paper & forest products',
            },
            {
                label: 'Performing arts',
                value: 'performing arts',
            },
            {
                label: 'Pharmaceuticals',
                value: 'pharmaceuticals',
            },
            {
                label: 'Philanthropy',
                value: 'philanthropy',
            },
            {
                label: 'Photography',
                value: 'photography',
            },
            {
                label: 'Plastics',
                value: 'plastics',
            },
            {
                label: 'Political organization',
                value: 'political organization',
            },
            {
                label: 'Primary/secondary education',
                value: 'primary/secondary education',
            },
            {
                label: 'Printing',
                value: 'printing',
            },
            {
                label: 'Professional training & coaching',
                value: 'professional training & coaching',
            },
            {
                label: 'Program development',
                value: 'program development',
            },
            {
                label: 'Public policy',
                value: 'public policy',
            },
            {
                label: 'Public relations and communications',
                value: 'public relations and communications',
            },
            {
                label: 'Public safety',
                value: 'public safety',
            },
            {
                label: 'Publishing',
                value: 'publishing',
            },
            {
                label: 'Railroad manufacture',
                value: 'railroad manufacture',
            },
            {
                label: 'Ranching',
                value: 'ranching',
            },
            {
                label: 'Real estate',
                value: 'real estate',
            },
            {
                label: 'Recreational facilities and services',
                value: 'recreational facilities and services',
            },
            {
                label: 'Religious institutions',
                value: 'religious institutions',
            },
            {
                label: 'Renewables & environment',
                value: 'renewables & environment',
            },
            {
                label: 'Research',
                value: 'research',
            },
            {
                label: 'Restaurants',
                value: 'restaurants',
            },
            {
                label: 'Retail',
                value: 'retail',
            },
            {
                label: 'Security and investigations',
                value: 'security and investigations',
            },
            {
                label: 'Semiconductors',
                value: 'semiconductors',
            },
            {
                label: 'Shipbuilding',
                value: 'shipbuilding',
            },
            {
                label: 'Sporting goods',
                value: 'sporting goods',
            },
            {
                label: 'Sports',
                value: 'sports',
            },
            {
                label: 'Staffing and recruiting',
                value: 'staffing and recruiting',
            },
            {
                label: 'Supermarkets',
                value: 'supermarkets',
            },
            {
                label: 'Telecommunications',
                value: 'telecommunications',
            },
            {
                label: 'Textiles',
                value: 'textiles',
            },
            {
                label: 'Think tanks',
                value: 'think tanks',
            },
            {
                label: 'Tobacco',
                value: 'tobacco',
            },
            {
                label: 'Translation and localization',
                value: 'translation and localization',
            },
            {
                label: 'Transportation/trucking/railroad',
                value: 'transportation/trucking/railroad',
            },
            {
                label: 'Utilities',
                value: 'utilities',
            },
            {
                label: 'Venture capital & private equity',
                value: 'venture capital & private equity',
            },
            {
                label: 'Veterinary',
                value: 'veterinary',
            },
            {
                label: 'Warehousing',
                value: 'warehousing',
            },
            {
                label: 'Wholesale',
                value: 'wholesale',
            },
            {
                label: 'Wine and spirits',
                value: 'wine and spirits',
            },
            {
                label: 'Wireless',
                value: 'wireless',
            },
            {
                label: 'Writing and editing',
                value: 'writing and editing',
            },
        ],
        inputPlaceholder: 'Accounting, Government, Management etc.',
    },
])

const parameters = reactive({})
const initialSearchPerformed = ref(false)
const waiting = ref(false)
const leadCount = ref(0)
const leadCountDisplay = ref(0)
const leadCountRangeDisplay = ref('')
const estimatedActualLeadCount = ref(0)
const showAdvanced = ref(false)
const applyingPresetFilters = ref(false)

const createEmptyFieldValue = (field) => {
    if(field.type === 'text') {
        return ''
    }

    if(field.type === 'multiText' || field.type === 'options' || field.type === 'search') {
        return []
    }

    return null
}

const initializeField = (field) => {
    if (typeof parameters[field.field] === 'undefined') {
        parameters[field.field] = createEmptyFieldValue(field)
    }
}

const normalizePresetString = (value) => {
    return String(value).trim().toLowerCase()
}

const getPresetValue = (field, rawValue) => {
    if (rawValue && typeof rawValue === 'object') {
        if (typeof rawValue.value !== 'undefined') {
            return rawValue.value
        }

        if (typeof rawValue.v !== 'undefined') {
            if (field.field === 'job_title') {
                return String(rawValue.v).replace(/^"(.*)"$/, '$1')
            }

            return rawValue.v
        }
    }

    return rawValue
}

const presetOptionAliases = {
    job_title_level: {
        'c-suite': { label: 'C-Level', value: 'CXO' },
        'c level': { label: 'C-Level', value: 'CXO' },
        'c-level': { label: 'C-Level', value: 'CXO' },
        'cxo': { label: 'C-Level', value: 'CXO' },
    },
}

const presetFieldAliases = {
    job_title_role: 'job_role',
}

const normalizePresetFilterValue = (field, rawValue) => {
    const value = getPresetValue(field, rawValue)

    if (value === null || typeof value === 'undefined' || value === '') {
        return null
    }

    if (field.type === 'multiText') {
        return {
            label: value,
            value,
        }
    }

    if (field.type === 'search') {
        return {
            label: value,
            value,
            type: String(value).includes(',') ? 'state' : 'country',
        }
    }

    if (field.type === 'options') {
        const normalizedValue = normalizePresetString(value)
        const aliasedOption = presetOptionAliases[field.field]?.[normalizedValue]

        if (aliasedOption) {
            return aliasedOption
        }

        const matchedOption = field.options?.find((option) => {
            return normalizePresetString(option.value) === normalizedValue
                || normalizePresetString(option.label) === normalizedValue
        })

        if (matchedOption) {
            return matchedOption
        }

        return {
            label: value,
            value,
        }
    }

    return value
}

const applyPresetFilters = () => {
    if (!props.presetFilters || Object.keys(props.presetFilters).length === 0) {
        return false
    }

    applyingPresetFilters.value = true

    const presetFilterEntries = Object.entries(props.presetFilters).reduce((accumulator, [field, value]) => {
        accumulator[presetFieldAliases[field] ?? field] = value

        return accumulator
    }, {})

    const presetFields = new Set(Object.keys(presetFilterEntries))
    showAdvanced.value = searchFields.value.some((field) => field.advanced && presetFields.has(field.field))

    searchFields.value.forEach((field) => {
        initializeField(field)

        if (!presetFields.has(field.field)) {
            return
        }

        const presetValues = Array.isArray(presetFilterEntries[field.field])
            ? presetFilterEntries[field.field]
            : [presetFilterEntries[field.field]]

        if (field.type === 'text') {
            parameters[field.field] = getPresetValue(field, presetValues[0]) ?? ''
            return
        }

        parameters[field.field] = presetValues
            .map((value) => normalizePresetFilterValue(field, value))
            .filter((value) => value !== null)
    })

    applyingPresetFilters.value = false

    return true
}

const addFieldOption = (field, option) => {
    if (field.field === 'location' && option.type === 'country' && option.states) {
        // Remove any existing states from this country
        parameters[field.field] = parameters[field.field].filter(item =>
            !option.states.some(state => state.value === item.value)
        );

        // Add all states from this country
        option.states.forEach(state => {
            if (!parameters[field.field].some(item => item.value === state.value)) {
                parameters[field.field].push({
                    label: state.label,
                    value: state.value,
                    type: 'state'
                });
            }
        });
    } else {
        // For individual state selection, just add it if not already present
        if (!parameters[field.field].some(item => item.value === option.value)) {
            parameters[field.field].push(option);
        }
    }
}

const removeFieldOption = (field, option) => {
    parameters[field.field] = parameters[field.field].filter((value) => value.value !== option.value)
}

const performQueryWithDebounce = debounce(() => {
    performQuery()
}, 1200)

const performQuery = async () => {
    let mergedSearch = Object.values(parameters).join('')
    if(mergedSearch === '' && mergedSearch.length < 2) {
        return;
    }

    waiting.value = true
    emit('waiting', waiting.value)
    initialSearchPerformed.value = true
    emit('initialSearchPerformed', true)
    const res = await axios.post(route('api.lead-hunter.search'), parameters)
    if (res.data.success) {
        leadCount.value = res.data.lead_count
        leadCountDisplay.value = res.data.lead_count_display
        leadCountRangeDisplay.value = res.data.lead_count_range_display
        estimatedActualLeadCount.value = res.data.estimated_actual_lead_count
    }
    waiting.value = false
    emit('parameters-updated', parameters)
    emit('waiting', waiting.value)
    emit('leadCount', leadCount.value)
    emit('leadCountDisplay', leadCountDisplay.value)
    emit('leadCountRangeDisplay', leadCountRangeDisplay.value)
    emit('estimatedActualLeadCount', estimatedActualLeadCount.value)
}

const addSuggestion = (parameter, suggestion) => {
    parameter.push({label: suggestion, value: suggestion})
}

watch(() => parameters, (newVal) => {
    if (applyingPresetFilters.value) {
        return
    }

    performQueryWithDebounce()
}, {deep: true})

onMounted(() => {
    searchFields.value.forEach((field) => {
        if (!field.advanced) {
            parameters[field.field] = createEmptyFieldValue(field)
        }
    })

    if (applyPresetFilters()) {
        performQuery()
    }
})

const initializeAdvancedField = (field) => {
    initializeField(field)
}

watch(showAdvanced, (newVal) => {
    if (newVal) {
        searchFields.value.forEach((field) => {
            if (field.advanced) {
                initializeAdvancedField(field)
            }
        })
    }
})

</script>

<template>
    <div class="grid gap-4 pb-4 lg:grid-cols-1">
        <template v-for="(field, i) in searchFields" :key="i">
            <div v-if="!field.advanced">
                <input-label :label="field.label"></input-label>

                <input-text
                    v-if="field.type === 'text'"
                    :placeholder="field.inputPlaceholder"
                    v-model:content="parameters[field.field]"
                ></input-text>

                <multi-text-field
                    v-if="field.type === 'multiText'"
                    :field="field"
                    :parameters="parameters"
                    :placeholder="field.inputPlaceholder"
                    @add-field-option="addFieldOption"
                    @remove-field-option="removeFieldOption"
                ></multi-text-field>

                <options-field
                    v-if="field.type === 'options'"
                    :field="field"
                    :parameters="parameters"
                    :placeholder="field.inputPlaceholder"
                    @add-field-option="addFieldOption"
                    @remove-field-option="removeFieldOption"
                ></options-field>

                <search-field
                    v-if="field.type === 'search'"
                    :field="field"
                    :search-url="field.searchUrl"
                    :parameters="parameters"
                    :placeholder="field.inputPlaceholder"
                    @add-field-option="addFieldOption"
                    @remove-field-option="removeFieldOption"
                ></search-field>

                <TitleCompletion
                    v-if="field.type === 'multiText' && parameters[field.field] && parameters[field.field].length > 0"
                    :input="parameters[field.field]"
                    @suggestion-selected="addSuggestion(parameters[field.field], $event)"
                ></TitleCompletion>
            </div>
        </template>

        <div class="mt-0">
            <button
                @click="showAdvanced = !showAdvanced"
                class="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
                <span>{{ showAdvanced ? 'Hide' : 'Show' }} advanced options</span>
                <svg
                    class="w-4 h-4 transition-transform"
                    :class="{ 'rotate-180': showAdvanced }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            <div v-show="showAdvanced" class="mt-4 space-y-4">
                <div class="flex items-center gap-2 p-2 italic text-gray-900 bg-white rounded-wl">
                    <span>When using the advanced search, it can greatly increase the accuracy, but it can also significantly decrease the number of leads.</span>
                </div>

                <template v-for="(field, i) in searchFields" :key="i">
                    <div v-if="field.advanced">
                        <input-label :label="field.label"></input-label>

                        <input-text
                            v-if="field.type === 'text'"
                            :placeholder="field.inputPlaceholder"
                            v-model:content="parameters[field.field]"
                        ></input-text>

                        <multi-text-field
                            v-if="field.type === 'multiText'"
                            :field="field"
                            :parameters="parameters"
                            :placeholder="field.inputPlaceholder"
                            @add-field-option="addFieldOption"
                            @remove-field-option="removeFieldOption"
                        ></multi-text-field>

                        <options-field
                            v-if="field.type === 'options'"
                            :field="field"
                            :parameters="parameters"
                            :placeholder="field.inputPlaceholder"
                            @add-field-option="addFieldOption"
                            @remove-field-option="removeFieldOption"
                        ></options-field>

                        <search-field
                            v-if="field.type === 'search'"
                            :field="field"
                            :search-url="field.searchUrl"
                            :parameters="parameters"
                            :placeholder="field.inputPlaceholder"
                            @add-field-option="addFieldOption"
                            @remove-field-option="removeFieldOption"
                        ></search-field>

                        <TitleCompletion
                            v-if="field.type === 'multiText' && parameters[field.field] && parameters[field.field].length > 0"
                            :input="parameters[field.field]"
                            @suggestion-selected="addSuggestion(parameters[field.field], $event)"
                        ></TitleCompletion>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<style scoped>
.rotate-180 {
    transform: rotate(180deg);
}
</style>
